import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import SocketController from '@/lib/socket';
import NotificationToast from '@/components/shared/NotificationToast';
import Router from './Router';
import CommandPalette from '@/components/shared/CommandPalette';
import ThemeProvider from '@/components/layout/ThemeProvider';
import { ConfirmDialogProvider } from '@/components/shared/ConfirmDialog';
import ImpersonationBanner from '@/features/sultan/ImpersonationBanner';
import { setAuthToken, getAuthToken } from '@/lib/trackizy';
import { applySession, fetchSession, fetchShellServer } from '@/features/enterprise/sessionBootstrap';
import useBrandColor from '@/hooks/useBrandColor';
import useBrandFavicon from '@/hooks/useBrandFavicon';
// NOTE: isSupabaseConfigured and setOrgToken are no longer needed in App.jsx
// since org context now comes from auth.session() via Trackizy Server.
import usePOIs from '@/features/pois/usePOIs';
import { isPublicPath } from './publicPaths';
import { initSentry } from '@/lib/sentry';

initSentry();

// Enable Arabic/Kurdish/Hebrew text shaping on all maps. The plugin URL is
// our worker-side wrapper, public/vendor/trackizy-rtl-text.js: it loads the
// vendored ICU plugin for bidi reordering only and swaps in
// public/vendor/trackizy-arabic-shaper.js for Arabic shaping, because ICU's
// u_shapeArabic leaves the letter before a Kurdish ە ڵ ڕ ێ unjoined
// (هەولێر drew an isolated heh). The ICU file (audit H8, 2026-10-14),
// public/vendor/mapbox-gl-rtl-text-0.2.3.min.js, is the byte-identical
// mapbox-gl-rtl-text.min.js from the @mapbox/mapbox-gl-rtl-text@0.2.3
// npm tarball (BSD-2-Clause), sha256
// 142f4fc31b4911887bacfea4df1813df67be28dfcb4c56e3f8f576f2e6fdf5d2.
// Everything is served same-origin so every nginx CSP keeps
// `script-src 'self'; worker-src 'self' blob:` with no third-party origin.
// Bump the ICU filename in the wrapper when upgrading that version.
maplibregl.setRTLTextPlugin('/vendor/trackizy-rtl-text.js', true);

export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.session.user);
  const [loading, setLoading] = useState(true);

  useBrandColor();
  useBrandFavicon();
  usePOIs();

  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const token = getAuthToken();
      if (!token) {
        // No stored token — redirect to login (public pages like /bus/:token
        // and /share/:token stay put; see publicPaths.js)
        if (!isPublicPath(pathname)) {
          navigate('/login', { replace: true });
        }
        setLoading(false);
        return;
      }
      try {
        // Validate token and get user + org context from Trackizy Server;
        // the shared bootstrap derives the session mode from the claims.
        const sessionData = await fetchSession();
        const server = await fetchShellServer(sessionData);
        applySession(sessionData, dispatch, { server });
      } catch {
        // Token invalid or expired — clear and redirect to login
        setAuthToken(null);
        if (!isPublicPath(pathname)) {
          navigate('/login', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: The old get-org-token Supabase Edge Function call is no longer needed.
  // Org context and sultan status are now included in the auth.session() response
  // from the Trackizy Server, handled in checkSession above.

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <ConfirmDialogProvider>
        <ImpersonationBanner />
        {user && <SocketController />}
        {user && <NotificationToast />}
        <Router />
        {user && <CommandPalette />}
      </ConfirmDialogProvider>
    </ThemeProvider>
  );
}
